import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AudioLayout from "../AudioLayout";
import { audioApi, formatErr } from "../api";
import { useAudioAuth } from "../AudioAuthContext";
import { Sparkles, Check, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AudioSubscription() {
  const { user, subscribed, subscription, refresh } = useAudioAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [pricing, setPricing] = useState({
    subscription_price: 50, currency: "INR",
    plan_name: "Single Plan",
    plan_description: "Unlimited access to Bala Basava Audio Comics",
    audio_mode: "PAID",
  });

  useEffect(() => {
    audioApi.get("/access-mode").then(r => setPricing(r.data)).catch(() => {});
  }, []);

  if (!user) {
    return (
      <AudioLayout>
        <div className="max-w-md mx-auto text-center py-16" data-testid="ac-sub-needs-login">
          <Lock className="mx-auto mb-4 text-amber-300" size={28}/>
          <h1 className="text-2xl font-bold">Sign in to subscribe</h1>
          <Link to="/audio-comics/login?next=/audio-comics/subscription" className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black font-semibold">Continue with Google</Link>
        </div>
      </AudioLayout>
    );
  }

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data: order } = await audioApi.post("/subscription/create-order", {});
      if (typeof window.Razorpay === "undefined") {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = res; s.onerror = rej;
          document.body.appendChild(s);
        });
      }
      const rz = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Bala Basava Audio Comics",
        description: pricing.plan_description,
        order_id: order.order_id,
        prefill: { name: user.name, email: user.email },
        theme: { color: "#fbbf24" },
        handler: async (response) => {
          try {
            await audioApi.post("/subscription/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Subscription activated!");
            await refresh();
            nav("/audio-comics/books", { replace: true });
          } catch (e) { toast.error(formatErr(e)); }
        },
      });
      rz.open();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const currencySymbol = pricing.currency === "INR" ? "₹" : pricing.currency;

  return (
    <AudioLayout>
      <div className="max-w-2xl mx-auto py-4">
        <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 mb-3">Subscription</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{pricing.plan_name}</h1>
        <p className="text-white/65 mt-3">{pricing.plan_description}</p>

        <div className="mt-8 rounded-2xl border border-white/10 bg-gradient-to-br from-fuchsia-500/10 via-amber-500/[0.04] to-transparent p-7" data-testid="ac-sub-card">
          <div className="flex items-baseline gap-2">
            <span className="text-5xl font-bold" data-testid="ac-sub-price">{currencySymbol}{pricing.subscription_price}</span>
            <span className="text-white/60">/month</span>
          </div>
          <ul className="mt-5 space-y-2 text-sm text-white/80">
            {[
              "Unlimited access to all books & vachanas",
              "Ad-free playback",
              "New stories added regularly",
              "Cancel anytime",
            ].map((x) => (
              <li key={x} className="flex items-center gap-2"><Check size={14} className="text-emerald-300"/> {x}</li>
            ))}
          </ul>

          {subscribed ? (
            <div className="mt-7 rounded-xl bg-emerald-500/10 border border-emerald-400/30 px-4 py-3 text-sm" data-testid="ac-sub-active">
              You&apos;re subscribed · expires {subscription?.expiry_date?.slice(0, 10)}
              <Link to="/audio-comics/books" className="block mt-2 text-amber-300 hover:text-amber-200">→ Open library</Link>
            </div>
          ) : (
            <button onClick={subscribe} disabled={busy} className="mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-amber-400 text-black font-semibold hover:bg-amber-300 disabled:opacity-60" data-testid="ac-sub-pay">
              {busy ? <Loader2 size={16} className="animate-spin"/> : <Sparkles size={16}/>}
              {busy ? "Processing…" : `Subscribe ${currencySymbol}${pricing.subscription_price} / month`}
            </button>
          )}
        </div>
        <p className="mt-3 text-[11px] text-white/45 text-center">Payments via Razorpay.</p>
      </div>
    </AudioLayout>
  );
}
